/**
 * Widget flotante de Smart Question.
 * Aparece abajo a la derecha cuando hay una pregunta pendiente.
 * El usuario puede responder, omitir, descartar.
 */
import { apiClient } from '../lib/api.js';

const h = (tag, attrs = {}, children = []) => {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') el.className = v;
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (v != null) el.setAttribute(k, v);
  }
  for (const c of [].concat(children)) {
    if (c == null) continue;
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
};

let mounted = false;
let pollHandle = null;

export function mountSmartQuestionsWidget() {
  if (mounted) return;
  mounted = true;
  const container = h('div', { id: 'smart-q-widget', class: 'fixed bottom-6 right-6 z-50 hidden' });
  document.body.appendChild(container);

  // Poll cada 2 minutos
  const tick = async () => {
    try {
      const { question } = await apiClient.get('/api/v1/questions/next');
      if (question) renderQuestion(container, question);
      else hide(container);
    } catch {
      // silent
    }
  };
  tick();
  pollHandle = setInterval(tick, 2 * 60 * 1000);
}

export function unmountSmartQuestionsWidget() {
  if (pollHandle) clearInterval(pollHandle);
  mounted = false;
  const el = document.getElementById('smart-q-widget');
  if (el) el.remove();
}

function hide(el) {
  el.classList.add('hidden');
  el.innerHTML = '';
}

function renderQuestion(container, q) {
  container.classList.remove('hidden');
  container.innerHTML = '';

  const card = h('div', {
    class: 'bg-base-800 border border-gold-400/40 rounded-2xl p-5 shadow-soft max-w-sm animate-slide-up',
  });

  // Header
  card.appendChild(h('div', { class: 'flex items-start justify-between gap-2 mb-2' }, [
    h('div', { class: 'flex items-center gap-2 text-xs uppercase tracking-widest text-gold-400' }, [
      h('span', {}, '💡'),
      h('span', {}, 'Tengo una pregunta'),
    ]),
    h('button', {
      class: 'text-ink-400 hover:text-ink-200 text-sm',
      title: 'Cerrar (volveré más tarde)',
      onclick: async () => {
        await apiClient.post(`/api/v1/questions/${q.id}/skip`);
        hide(container);
      },
    }, '✕'),
  ]));

  card.appendChild(h('h4', { class: 'font-display font-semibold text-base mb-1' }, q.question));
  if (q.explanation) {
    card.appendChild(h('p', { class: 'text-xs text-ink-300 mb-3' }, q.explanation));
  }

  // Input según tipo (siempre devuelve string para que Carlos pueda dar contexto)
  let inputEl;
  let extraEl;        // textarea opcional con explicación adicional
  let getValue = () => '';
  if (q.answerType === 'number') {
    // Aunque sea numérica, dejamos input de texto (Carlos quiere poder dar contexto).
    // El backend acepta string libre y lo persiste.
    inputEl = h('input', { type: 'text', inputmode: 'decimal', class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-ink-100 focus:border-gold-400 focus:outline-none mb-2', placeholder: 'Respuesta (p.ej. "3" o "3 dobles + 1 individual")' });
    getValue = () => inputEl.value;
  } else if (q.answerType === 'choice') {
    inputEl = h('div', { class: 'space-y-1 mb-3' });
    let selected = null;
    (q.choices || []).forEach((c) => {
      const btn = h('button', {
        type: 'button',
        class: 'block w-full text-left px-3 py-2 rounded-lg bg-base-700 hover:bg-base-600 text-sm border border-base-600',
        onclick: () => {
          selected = c;
          [...inputEl.children].forEach((b) => b.className = 'block w-full text-left px-3 py-2 rounded-lg bg-base-700 hover:bg-base-600 text-sm border border-base-600');
          btn.className = 'block w-full text-left px-3 py-2 rounded-lg bg-gold-400/20 text-gold-200 text-sm border border-gold-400/50';
        },
      }, c);
      inputEl.appendChild(btn);
    });
    getValue = () => selected;
  } else if (q.answerType === 'date') {
    inputEl = h('input', { type: 'date', class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-ink-100 focus:border-gold-400 focus:outline-none mb-2' });
    getValue = () => inputEl.value;
  } else if (q.answerType === 'boolean') {
    let sel = null;
    inputEl = h('div', { class: 'flex gap-2 mb-3' }, [
      h('button', { type: 'button', class: 'flex-1 px-3 py-2 rounded-lg bg-base-700 hover:bg-emerald-400/20 hover:text-emerald-300 text-sm border border-base-600', onclick: (e) => { sel = 'yes'; e.target.className = 'flex-1 px-3 py-2 rounded-lg bg-emerald-400/20 text-emerald-300 text-sm border border-emerald-400/50'; } }, 'Sí'),
      h('button', { type: 'button', class: 'flex-1 px-3 py-2 rounded-lg bg-base-700 hover:bg-red-400/20 hover:text-red-300 text-sm border border-base-600', onclick: (e) => { sel = 'no'; e.target.className = 'flex-1 px-3 py-2 rounded-lg bg-red-400/20 text-red-300 text-sm border border-red-400/50'; } }, 'No'),
    ]);
    getValue = () => sel;
  } else {
    inputEl = h('textarea', { class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-ink-100 focus:border-gold-400 focus:outline-none mb-2 min-h-[60px]', placeholder: 'Escribe tu respuesta…' });
    getValue = () => inputEl.value;
  }
  card.appendChild(inputEl);

  // Campo de explicación/contexto SIEMPRE disponible (no solo numérico).
  // Carlos quiere poder dar matices a cualquier respuesta.
  if (q.answerType !== 'choice' && q.answerType !== 'boolean') {
    extraEl = h('textarea', {
      class: 'w-full bg-base-900/60 border border-base-700 rounded-lg px-3 py-2 text-ink-200 focus:border-gold-400 focus:outline-none mb-3 min-h-[50px] text-xs',
      placeholder: '+ Añade contexto o explicación (opcional)…',
    });
    card.appendChild(extraEl);
  }

  // Footer
  card.appendChild(h('div', { class: 'flex items-center justify-between text-xs' }, [
    h('div', { class: 'flex gap-3' }, [
      h('button', {
        class: 'text-ink-400 hover:text-ink-200',
        onclick: async () => {
          await apiClient.post(`/api/v1/questions/${q.id}/skip`);
          hide(container);
        },
      }, 'Más tarde'),
      h('button', {
        class: 'text-ink-400 hover:text-red-400',
        onclick: async () => {
          if (confirm('¿No volver a preguntar esto?')) {
            await apiClient.post(`/api/v1/questions/${q.id}/dismiss`);
            hide(container);
          }
        },
      }, 'No preguntar'),
    ]),
    h('button', {
      class: 'bg-gradient-to-r from-gold-400 to-gold-500 text-base-900 font-semibold px-4 py-1.5 rounded-lg text-sm',
      onclick: async () => {
        const v = getValue();
        const extra = extraEl ? extraEl.value.trim() : '';
        if ((v == null || v === '') && !extra) return;
        // Concatenar respuesta + contexto si existe (separado por " — ")
        const finalAnswer = extra ? `${v} — ${extra}` : String(v);
        await apiClient.post(`/api/v1/questions/${q.id}/answer`, { answer: finalAnswer });
        hide(container);
      },
    }, 'Guardar'),
  ]));

  container.appendChild(card);
}
